<template>
  <view>
    <view class="c-float-tools" :style="computeStyle">
      <!-- 浮窗 -->
      <view
        v-if="computeFloatingWindowWhiteList && isOpenFloatingWindow && floatingWindowData"
        class="item"
        @click="handleCouponClick"
      >
        <view class="coupon-item" :style="computeCouponItemStyle">
          <!-- 已领取 -->
          <c-img
            v-if="isReceivedFloatingWindowCoupon"
            :src="floatingWindowData.floatingWindowImageReceived"
          />
          <!-- 未领取 -->
          <c-img v-else :src="floatingWindowData.floatingWindowImage" />
        </view>
      </view>

      <!-- 分享赚 -->
      <c-img
        v-if="computeShareEarnWhiteList && appStore.distributorData && !shareEarnOptions.hidden"
        class="item"
        remote-name="home_share"
        @click="handleShareClick"
      />

      <c-img
        v-if="showShoppingCart"
        class="item"
        remote-name="home_cart"
        @click="handleShoppingCartClick"
      />

      <view v-if="showCustomerService" class="item" @click="handleCustomerServiceClick">
        <view class="common-inner service-customer">








          <c-img class="service-icon" :size="48" remote-name="customer" />
        </view>
      </view>
    </view>

    <share-earn-popup ref="shareEarnPopupRef" :share-value="shareEarnOptions.shareValue" />
  </view>
</template>

<script lang="ts">
// ===== 模块级缓存（所有 float-tools 实例共享，整个会话有效） =====
let _floatingWindowCache: AnyObject | null = null;
// 领取状态缓存：一旦为 true（已领取），后续不再请求（领取状态不可逆）
let _isReceivedCouponCache = false;
</script>

<script setup lang="ts">
import ShareEarnPopup from '@/components/share-earn-popup/index.vue';
import { PAGE_PATH } from '@/constants';
import { fetchFloatingWindow, fetchGetFloatingWindowCouponRecord } from '@/services';
import { useAppStore, useUserStore } from '@/store';
import { businessUtils, commonUtils } from '@/utils';
import { onShow } from '@dcloudio/uni-app';
import { computed, ref, toRefs } from 'vue';
import { CustomJumpType, CustomNoticeScene } from '@/types';
import Auth from '@/components/auth/index.vue';

interface Props {
  position?: {
    bottom?: number;
    right?: number;
  };
  showCustomerService?: boolean;
  showShoppingCart?: boolean;
  /** 分享赚数据 */
  shareEarnOptions?: {
    shareValue: string;
    hidden?: boolean;
  };
}
const props = withDefaults(defineProps<Props>(), {
  showCustomerService: false,
  showShoppingCart: false,
  shareEarnOptions: () => ({
    shareValue: '',
    hidden: false,
  }),
});
const { position, shareEarnOptions, showCustomerService, showShoppingCart } = toRefs(props);

const userStore = useUserStore();
const appStore = useAppStore();

// 分享赚弹窗
const shareEarnPopupRef = ref<InstanceType<typeof ShareEarnPopup>>();
// 优惠券浮窗数据
const floatingWindowData = ref<AnyObject>();
// 是否开启优惠券浮窗
const isOpenFloatingWindow = ref<boolean>(false);
// 是否已领取优惠券
const isReceivedFloatingWindowCoupon = ref<boolean>(false);

/** 浮窗样式 */
const computeStyle = computed(() => {
  let style = {};

  if (position?.value) {
    style = {
      ...style,
      bottom: `${position.value.bottom}rpx`,
      right: `${position.value.right}rpx`,
    };
  }

  return style;
});

/** 浮窗优惠券样式 */
const computeCouponItemStyle = computed(() => {
  let style = {};

  const _data = floatingWindowData.value;
  if (_data?.width && _data?.height) {
    style = {
      width: `${_data.width * 2}rpx`,
      height: `${_data.height * 2}rpx`,
    };
  }

  return style;
});

/** 浮窗白名单 */
const computeFloatingWindowWhiteList = computed(() => {
  const pages = getCurrentPages();
  const currentPage = `/${pages[pages.length - 1].route}`;
  const isMatch = [PAGE_PATH.activity.route, PAGE_PATH.commodity.route].includes(currentPage);
  return commonUtils.tabbar.is() || isMatch;
});

// 分享赚白名单
const computeShareEarnWhiteList = computed(() => {
  const pages = getCurrentPages();
  const currentPage = `/${pages[pages.length - 1].route}`;
  return [
    PAGE_PATH.home.route,
    PAGE_PATH.classify.route,
    PAGE_PATH.activity.route,
    PAGE_PATH.searchResult.route,
    PAGE_PATH.memberDetail.route,
    PAGE_PATH.member.route,
    PAGE_PATH.commodity.route,
    PAGE_PATH.blindBoxActivity.route,
    PAGE_PATH.integralCommodity.route,
    PAGE_PATH.couponDetail.route,
    PAGE_PATH.signIn.route,
    PAGE_PATH.forumNoteDetail.route,
    PAGE_PATH.forumTopicDetail.route,
    PAGE_PATH.blindBoxActivityV2.route,
    PAGE_PATH.raffle.route,
  ].includes(currentPage);
});

/** 浮窗优惠券点击 */
const handleCouponClick = () => {
  // console.log('浮窗优惠券点击', floatingWindowData.value);
  // 触发自定义订阅通知授权
  businessUtils.customNotice.trigger(CustomNoticeScene.floatingWindow);
  businessUtils.mp.customJump(
    floatingWindowData.value?.jumpValue,
    floatingWindowData.value?.jumpType
  );
};

/** 分享赚点击 */
const handleShareClick = () => {
  businessUtils.matomo.customPageClick();
  shareEarnPopupRef.value?.open();
};

/** 浮窗优惠券处理 */
const couponHandler = async () => {
  try {
    // 非浮窗白名单不处理
    if (!computeFloatingWindowWhiteList.value) return;

    // ===== 浮窗配置：整个会话只请求一次 =====
    if (!_floatingWindowCache) {
      _floatingWindowCache = await fetchFloatingWindow();
    }
    const floatingWindowRes = _floatingWindowCache;

    // ===== 领取记录：已领取后不再请求（领取状态不可逆），未领取时每次请求 ===== (userStore.isLogin兼容h5未登录场景，避免请求失败)
    if (floatingWindowRes?.jumpType === CustomJumpType.coupon && userStore.isLogin) {
      if (!_isReceivedCouponCache) {
        // 未领取，每次请求检查状态
        const result = await fetchGetFloatingWindowCouponRecord();
        if (result) {
          _isReceivedCouponCache = true; // 已领取，后续不再请求
        }
        isReceivedFloatingWindowCoupon.value = result;
      } else {
        // 已领取，直接使用缓存
        isReceivedFloatingWindowCoupon.value = true;
      }
    }

    // 是否开启
    let isOpen = floatingWindowRes?.isOpen;
    // 如果是领取后不开启，且用户已领取，则最终不开启
    if (!floatingWindowRes?.isShowAfterReceived && isReceivedFloatingWindowCoupon.value) {
      isOpen = false;
    }

    isOpenFloatingWindow.value = isOpen;
    floatingWindowData.value = floatingWindowRes ?? undefined;
  } catch (error) {
    console.error(`error: ${error}`);
  }
};

/** 购物车点击 */
const handleShoppingCartClick = () => {
  uni.reLaunch({ url: PAGE_PATH.cart.route });
};

/** 客服点击 */
const handleCustomerServiceClick = () => {

  businessUtils.qiyu.initConfig();
  businessUtils.qiyu.open();

};

onShow(() => {
  couponHandler();
});
</script>

<style scoped lang="scss">
.c-float-tools {
  position: fixed;
  right: var(--window-right);
  bottom: 350rpx;
  z-index: 991;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 152rpx;
    height: 152rpx;
    margin-bottom: 40rpx;

    &:last-child {
      margin-bottom: 0;
    }

    .coupon-item {
      width: 180rpx;
      height: 180rpx;
    }

    .common-inner {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100rpx;
      height: 100rpx;
      border-radius: 50%;
      background: $W;
      @include shadow;
    }
  }
}
</style>
